/* ============================================================
   VERA VEIL | HANDMADE — Custom Theme JS v2
   ============================================================ */

(function () {
  'use strict';

  /* ── Scroll Reveal ──────────────────────────────────────── */
  function initScrollReveal() {
    document.body.classList.add('js-ready');

    const selectors = '.vv-up, .vv-slide-left, .vv-slide-right, .vv-scale-in, .vv-reveal';
    const elements = document.querySelectorAll(selectors);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    // Fallback: reveal all after 2.5s
    setTimeout(() => {
      elements.forEach((el) => el.classList.add('in'));
    }, 2500);
  }

  /* ── Lazy Videos ────────────────────────────────────────── */
  function initLazyVideos() {
    const videos = document.querySelectorAll('.vv-lazy-video');
    if (!videos.length) return;

    function load(video) {
      if (video.dataset.vvLoaded) return;
      video.dataset.vvLoaded = '1';
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
      const playing = video.play();
      if (playing) playing.catch(() => {});
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            load(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '300px 0px' }
    );

    videos.forEach((video) => observer.observe(video));
  }

  /* ── Cursor Trail (subtle luxury effect) ────────────────── */
  function initCursorTrail() {
    if (window.matchMedia('(pointer: coarse)').matches) return; // skip mobile
    const dot = document.createElement('div');
    dot.className = 'vv-cursor';
    document.body.appendChild(dot);

    let mx = 0, my = 0, cx = 0, cy = 0;
    document.addEventListener('mousemove', (e) => {
      mx = e.clientX; my = e.clientY;
    });

    function animateCursor() {
      cx += (mx - cx) * 0.12;
      cy += (my - cy) * 0.12;
      dot.style.transform = `translate(${cx}px,${cy}px)`;
      requestAnimationFrame(animateCursor);
    }
    animateCursor();

    document.addEventListener('mousedown', () => dot.classList.add('pressed'));
    document.addEventListener('mouseup',   () => dot.classList.remove('pressed'));

    // Enlarge on links/buttons
    document.querySelectorAll('a, button').forEach(el => {
      el.addEventListener('mouseenter', () => dot.classList.add('hover'));
      el.addEventListener('mouseleave', () => dot.classList.remove('hover'));
    });
  }

  /* ── Hero Overlay — transparent nav over full-bleed hero ── */
  function initHeroOverlay() {
    const hero = document.querySelector('.vv-hero');
    if (!hero) return;

    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;

    // Add body class so CSS can scope styles
    document.body.classList.add('vv-hero-overlay');

    // The hero geometry itself lives in CSS and reads --vv-header-h, which the
    // inline script in theme.liquid sets before the hero is parsed. Only keep
    // it in sync when the header can actually change height.
    function syncHeaderHeight() {
      document.documentElement.style.setProperty(
        '--vv-header-h',
        wrapper.getBoundingClientRect().height + 'px'
      );
    }

    window.addEventListener('resize', syncHeaderHeight, { passive: true });
  }

  /* ── Sticky Header ──────────────────────────────────────── */
  function initStickyHeader() {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;

    const hero = document.querySelector('.vv-hero');

    function getThreshold() {
      // Stay transparent until 75% of hero is scrolled past
      return hero ? hero.offsetHeight * 0.75 : 60;
    }

    function updateHeader() {
      if (window.scrollY > getThreshold()) {
        wrapper.classList.add('scrolled-past-header');
      } else {
        wrapper.classList.remove('scrolled-past-header');
      }
    }

    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
    window.addEventListener('resize', updateHeader, { passive: true });
  }

  /* ── Mobile Menu ────────────────────────────────────────── */
  function initMobileMenu() {
    const openBtn  = document.querySelector('.vv-mobile-menu-btn');
    const closeBtn = document.querySelector('.vv-mobile-menu__close');
    const menu     = document.querySelector('.vv-mobile-menu');
    if (!openBtn || !menu) return;

    openBtn.addEventListener('click', () => {
      menu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        menu.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }

    menu.addEventListener('click', (e) => {
      if (e.target === menu) {
        menu.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    });
  }

  /* ── Parallax Band ──────────────────────────────────────── */
  function initParallax() {
    const imgs = document.querySelectorAll('.vv-band__img');
    if (!imgs.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    function onScroll() {
      imgs.forEach((img) => {
        const band = img.closest('.vv-band');
        if (!band) return;
        const rect   = band.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        const shift  = Math.max(-50, Math.min(50, center * 0.15));
        img.style.transform = `translateY(${shift}px)`;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── FAQ Accordion ──────────────────────────────────────── */
  function initAccordion() {
    const items = document.querySelectorAll('.vv-acc__item');
    if (!items.length) return;

    items.forEach((item) => {
      const btn = item.querySelector('.vv-acc__btn');
      if (!btn) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('open');
        items.forEach((i) => i.classList.remove('open'));
        if (!isOpen) item.classList.add('open');
      });
    });
  }

  /* ── Announcement Bar close ─────────────────────────────── */
  function initAnnouncementBar() {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    const closeBtn = bar.querySelector('[data-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        bar.style.transition = 'height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
        bar.style.overflow = 'hidden';
        bar.style.height = bar.offsetHeight + 'px';
        requestAnimationFrame(() => {
          bar.style.height = '0';
          bar.style.opacity = '0';
          bar.style.padding = '0';
        });
      });
    }
  }

  /* ── Product gallery thumbs ─────────────────────────────── */
  function initProductGallery() {
    const mainImg = document.querySelector('.vv-pdp__main-img');
    const thumbs  = document.querySelectorAll('.vv-pdp__thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        if (src) {
          mainImg.style.opacity = '0';
          setTimeout(() => {
            mainImg.src = src;
            mainImg.style.opacity = '1';
          }, 200);
        }
        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ── Newsletter form ────────────────────────────────────── */
  function initNewsletterForm() {
    const forms = document.querySelectorAll('.vv-newsletter__form');
    forms.forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        const btn   = form.querySelector('button[type="submit"]');
        if (!input || !input.value) return;
        if (btn) {
          btn.textContent = 'Thank you';
          btn.disabled = true;
        }
        input.value = '';
      });
    });
  }

  /* ── Bespoke form ───────────────────────────────────────── */
  function initBespokeForm() {
    const form = document.querySelector('.vv-bespoke-form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('.vv-form__submit');
      if (btn) {
        btn.textContent = 'Request Sent';
        btn.disabled = true;
        btn.style.opacity = '0.6';
      }
    });
  }

  /* ── Header Dropdowns ───────────────────────────────────── */
  function initHeaderHoverMenu() {
    // The parent title is a link now, so a click must navigate — that only
    // leaves hover to open the submenu, and only where hover exists.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.header__inline-menu header-menu').forEach((menu) => {
      const details = menu.querySelector('details');
      if (!details) return;

      menu.addEventListener('mouseenter', () => {
        details.open = true;
      });
      menu.addEventListener('mouseleave', () => {
        details.open = false;
      });
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  function init() {
    initHeaderHoverMenu();
    initHeroOverlay();
    initStickyHeader();
    initCursorTrail();
    initMobileMenu();
    initScrollReveal();
    initLazyVideos();
    initParallax();
    initAccordion();
    initAnnouncementBar();
    initProductGallery();
    initNewsletterForm();
    initBespokeForm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
